import {Box, useTheme} from '@mui/material';
import {FC, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {ColoredStatus} from 'src/components/common/ColoredStatus';
import {statusColors} from 'src/components/common/ColoredStatus/ColoredStatus.hook';
import {ConnectorStatusEnum} from 'src/enums';
import {ConnectorDTO} from 'src/types';
import {fusedSx, segmentSx} from './ConnectorStatusPill.sx';
import {ConnectorStatusPillType} from './ConnectorStatusPill.types';

/**
 * Two letters off the OCPP name, the same in every language the interface speaks.
 *
 * Not the first letter of the translated word: in French four statuses begin with E, in English
 * Faulted and Finishing both begin with F, and the two Suspendeds are indistinguishable in all four
 * languages - so the mark would collide, and would change as the reader switched language. These are
 * a small vocabulary to learn, which is why the status is still spelled out in the reader's own
 * language on hover and on the charger's page.
 */
const STATUS_CODE: Record<string, string> = {
    [ConnectorStatusEnum.AVAILABLE]: 'AV',
    [ConnectorStatusEnum.CHARGING]: 'CH',
    [ConnectorStatusEnum.PREPARING]: 'PR',
    [ConnectorStatusEnum.FINISHING]: 'FI',
    [ConnectorStatusEnum.FAULTED]: 'FA',
    [ConnectorStatusEnum.SUSPENDED_EV]: 'EV',
    [ConnectorStatusEnum.SUSPENDED_EVSE]: 'ES',
    [ConnectorStatusEnum.UNAVAILABLE]: 'UN',
    [ConnectorStatusEnum.RESERVED]: 'RE',
    [ConnectorStatusEnum.UNDEFINED]: 'UD',
};

/**
 * Every socket of one charger, in one pill.
 *
 * The list used to show `connectors[0].currentStatus` - one socket of however many, and not even a
 * predictable one, since the server returns connectors in an unordered `Set`. A station charging on
 * one socket and faulted on the other looked healthy.
 *
 * A segment carries the status, in colour and in two letters; its **position** carries the socket,
 * first segment being connector 1, which is why they are sorted here rather than taken as they come.
 * Both are named in full on hover and to a screen reader. One socket keeps the ordinary pill and its
 * whole word - a single lettered segment would be a code where there was room to say it.
 */
export const ConnectorStatusPill: FC<ConnectorStatusPillType> = ({connectors, fontSize = 14}) => {
    const intl = useIntl();
    const theme = useTheme();

    const sockets = useMemo(
        () => [...(connectors ?? [])].sort((a, b) => a.connectorId - b.connectorId),
        [connectors]
    );

    const describe = (connector: ConnectorDTO) => [
        intl.formatMessage({id: 'connectorNumber'}, {number: connector.connectorId}),
        intl.formatMessage({id: connector.currentStatus}),
    ].join(' — ');

    if (!sockets.length) {
        return null;
    }

    if (sockets.length === 1) {
        return <ColoredStatus label={sockets[0].currentStatus} fontSize={fontSize}/>;
    }

    return (
        <Box role="img" aria-label={sockets.map(describe).join(', ')} sx={fusedSx}>
            {sockets.map((connector) => (
                <Box
                    key={connector.id}
                    component="span"
                    title={describe(connector)}
                    sx={segmentSx(statusColors(theme, connector.currentStatus), fontSize)}
                >
                    {STATUS_CODE[connector.currentStatus] ?? STATUS_CODE[ConnectorStatusEnum.UNDEFINED]}
                </Box>
            ))}
        </Box>
    );
};
